/**
 * A contagem do `NumeroAnimado` (INDICADORES-E-CONTRATOS, 06/10/2026) — em módulo próprio porque hook
 * exportado de arquivo de componente derruba o lint (`react-refresh/only-export-components`).
 *
 *  - ao carregar, conta de 0 até o valor; ao trocar o filtro, TRANSITA do valor anterior para o novo;
 *  - 550 ms (medido: com o primeiro desenho da tela, 600 ms chegavam a 709 ms na prova do navegador), curva `easeOutCubic` (a mesma dos gráficos do Financeiro);
 *  - `prefers-reduced-motion`: o valor aparece direto.
 */
import { useEffect, useRef, useState } from 'react';

import { easeOutCubic, useReducedMotion } from '@/pages/admin/financeiro/graficos/motion';

/** A duração da contagem, em ms — entre os 300 e 700 que o handoff pede. */
export const DURACAO_NUMERO_MS = 550;

export function useNumeroAnimado(alvo: number, duracaoMs = DURACAO_NUMERO_MS): number {
  const reduzido = useReducedMotion();
  const [valor, setValor] = useState(reduzido ? alvo : 0);
  const atual = useRef(reduzido ? alvo : 0);
  const quadro = useRef(0);

  useEffect(() => {
    if (reduzido) {
      atual.current = alvo;
      setValor(alvo);
      return;
    }
    const de = atual.current;
    if (de === alvo) return;
    // ⚠️ O relógio começa no PRIMEIRO QUADRO, não no `performance.now()` do efeito: o `requestAnimationFrame`
    // entrega a hora do início do quadro, que pode ser ANTERIOR a ele — o progresso nascia negativo e a curva
    // levava o número para baixo de zero ("0 → −2 → 0 → 2", "−R$ 12.221,84"; medido pelo
    // `scripts/prova-da-animacao.mjs`, 06/10/2026). E o progresso fica preso entre 0 e 1.
    let inicio: number | null = null;
    cancelAnimationFrame(quadro.current);
    const passo = (t: number) => {
      if (inicio === null) inicio = t;
      const p = Math.min(1, Math.max(0, (t - inicio) / duracaoMs));
      const v = de + (alvo - de) * easeOutCubic(p);
      atual.current = v;
      setValor(v);
      if (p < 1) quadro.current = requestAnimationFrame(passo);
    };
    quadro.current = requestAnimationFrame(passo);
    return () => cancelAnimationFrame(quadro.current);
  }, [alvo, reduzido, duracaoMs]);

  return valor;
}
